import { useTranslation } from "react-i18next";
import "../../../i18n";
import { CompanyChip, StatusPill } from "../../../design/components";
import type { DocumentDetail } from "../../../api/types";
import { familyShort } from "../../../domain/families";
import { DocTypeCell } from "../DocTypeCell";
import { statePill } from "../DocumentRow";
import { RowActions } from "../RowActions";
import styles from "./DocumentInspector.module.css";

function CopyId({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <span className={styles.idPair}>
      <span className={styles.sub}>{label}</span>
      <span className="mono">{value}</span>
      <button
        type="button"
        className={styles.copy}
        aria-label={t("documents.inspector.header.copyLabel", "Copy {{label}}", { label })}
        onClick={() => void navigator.clipboard?.writeText(value)}
      >
        {t("documents.inspector.header.copy", "Copy")}
      </button>
    </span>
  );
}

export function Header({ doc }: { doc: DocumentDetail }) {
  const { t } = useTranslation();
  const DIRECTION_LABEL = {
    out: t("documents.inspector.header.outbound", "Outbound"),
    in: t("documents.inspector.header.inbound", "Inbound"),
  } as const;
  return (
    <div className="page-head">
      <div className="grow">
        <h1 className="display">{doc.invoiceNumber ?? doc.erpDocId ?? doc.id}</h1>
        <div className={styles.headMeta}>
          <CompanyChip entityId={doc.entityId} name={doc.entityName} code={doc.entityCode} />
          <StatusPill {...statePill(doc.state)} />
          <span className={styles.sub}>
            {DIRECTION_LABEL[doc.direction]} ·{" "}
            <DocTypeCell typeCode={doc.typeCode} flags={doc.flags} direction={doc.direction} />
          </span>
          {doc.profileVersionId ? (
            <span className={styles.sub}>
              {t("documents.inspector.header.mappingProfile", "Mapping profile {{family}} · {{version}}", {
                family: familyShort(doc.family),
                version: doc.profileVersionId,
              })}
            </span>
          ) : null}
        </div>
        <div className={styles.ids}>
          {doc.erpDocId ? (
            <CopyId label={t("documents.inspector.header.erpDocument", "ERP document")} value={doc.erpDocId} />
          ) : null}
          {doc.uuid ? <CopyId label={t("documents.inspector.header.uuid", "UUID")} value={doc.uuid} /> : null}
        </div>
      </div>
      <RowActions row={doc} />
    </div>
  );
}
